/**
 * User Settings — force email/password change (admin action).
 *
 * Shared by the "Configurações" tab on equipe and alunos single resources:
 * confirms the action, POSTs to the given REST endpoint, then shows the
 * result (including the generated password, when applicable, since it is
 * never emailed in plaintext).
 *
 * @global window.lbChangeUserEmail
 * @global window.lbChangeUserPassword
 * @package Labead
 */

(function () {
	'use strict';

	function escapeHtml(str) {
		return String(str == null ? '' : str)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	async function putJson(url, body) {
		var res = await fetch(url, {
			method: 'PUT',
			headers: {
				'X-WP-Nonce': lb_data.rest_nonce,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(body),
		});
		return res.json();
	}

	// Erro do campo (vazio, inválido, e-mail já usado…): destaque no campo
	// quando quem chamou passou `opts.campo`; sem ele, toast.
	function avisarCampo(campo, msg) {
		if (campo && window.lbDestacarCampo) window.lbDestacarCampo(campo, msg);
		else lbAlert.toast({ color: 'w', message: msg });
	}

	/**
	 * Force-change a user's email.
	 *
	 * @param {Object} opts
	 * @param {string} opts.url      REST endpoint (PUT) for the change.
	 * @param {string} opts.postKey  'alunos' | 'equipe'.
	 * @param {string} opts.newEmail New email address.
	 * @param {boolean} opts.notify  Whether to send the "aviso" notice email.
	 * @return {Promise<boolean>} Whether the change was confirmed and applied.
	 */
	window.lbChangeUserEmail = async function (opts) {
		var newEmail = (opts.newEmail || '').trim();
		if (!newEmail) {
			avisarCampo(opts.campo, 'Informe o novo e-mail.');
			return false;
		}

		var confirmed = await lbAlert.confirm({
			color: 'w',
			html: '<h4>Alterar e-mail de acesso</h4>'
				+ '<p>O e-mail será alterado imediatamente para <strong>' + escapeHtml(newEmail) + '</strong>, sem exigir confirmação da pessoa.</p>'
				+ (opts.notify ? '<p class="lw:text-xs lw:opacity-70">Um e-mail de aviso será enviado para o novo endereço.</p>' : ''),
			confirmButtonText: 'Confirmar alteração',
		});
		if (!confirmed.isConfirmed) return false;

		var data;
		try {
			data = await putJson(opts.url, { post_key: opts.postKey, new_email: newEmail, notify: !!opts.notify });
		} catch (e) {
			lbAlert.toast({ color: 'd', message: 'Erro ao alterar o e-mail.' });
			return false;
		}

		if (!data.success) {
			// Recusa do servidor aqui é sobre o e-mail digitado (inválido, já em uso).
			if (opts.campo && data.message) avisarCampo(opts.campo, data.message);
			else lbAlert.toast({ color: 'd', message: data.message || 'Erro ao alterar o e-mail.' });
			return false;
		}

		lbAlert.toast({ color: 's', message: data.message || 'E-mail alterado com sucesso.' });
		if (typeof opts.onSuccess === 'function') opts.onSuccess(data.data || {});
		return true;
	};

	/**
	 * Force-change a user's password.
	 *
	 * @param {Object} opts
	 * @param {string} opts.url         REST endpoint (PUT) for the change.
	 * @param {string} opts.postKey     'alunos' | 'equipe'.
	 * @param {string} [opts.newPassword] Typed password (ignored when opts.generate is true).
	 * @param {boolean} [opts.generate] Auto-generate a random password server-side.
	 * @param {boolean} opts.notify     Whether to send the "aviso" notice email.
	 * @return {Promise<boolean>} Whether the change was confirmed and applied.
	 */
	window.lbChangeUserPassword = async function (opts) {
		if (!opts.generate && (!opts.newPassword || opts.newPassword.length < 8)) {
			avisarCampo(opts.campo, 'A senha deve ter no mínimo 8 caracteres.');
			return false;
		}

		var confirmed = await lbAlert.confirm({
			color: 'w',
			html: '<h4>Alterar senha</h4>'
				+ '<p>A senha será alterada imediatamente, sem exigir confirmação da pessoa. As sessões ativas dessa pessoa serão encerradas.</p>'
				+ (opts.notify ? '<p class="lw:text-xs lw:opacity-70">Um e-mail de aviso será enviado com a senha.</p>' : ''),
			confirmButtonText: 'Confirmar alteração',
		});
		if (!confirmed.isConfirmed) return false;

		var data;
		try {
			data = await putJson(opts.url, {
				post_key: opts.postKey,
				new_password: opts.generate ? '' : opts.newPassword,
				generate: !!opts.generate,
				notify: !!opts.notify,
			});
		} catch (e) {
			lbAlert.toast({ color: 'd', message: 'Erro ao alterar a senha.' });
			return false;
		}

		if (!data.success) {
			lbAlert.toast({ color: 'd', message: data.message || 'Erro ao alterar a senha.' });
			return false;
		}

		var generated = (data.data && data.data.new_password) || '';
		if (generated) {
			await lbAlert.confirm({
				color: 's',
				showCancelButton: false,
				confirmButtonText: 'Fechar',
				html: '<h4>Senha gerada</h4>'
					+ '<p>Copie a senha abaixo — ela não será exibida novamente:</p>'
					+ '<p class="lw:text-sm lw:font-mono lw:break-all lw:px-2 lw:py-1 lw:rounded lw:mt-1 lw:mb-1.5" style="background: rgba(var(--lbm-text-rgb), 0.14);">' + escapeHtml(generated) + '</p>'
					+ '<a href="javascript:void(0)" lb-copy="' + escapeHtml(generated) + '" class="lw:inline-flex lw:items-center lw:gap-1 lw:text-xs lw:font-medium lw:no-underline lw:hover:underline lw:cursor-pointer">'
					+ '<i data-lucide="copy" class="lw:w-3 lw:h-3"></i> Copiar senha</a>',
			});
			if (window.lbLucide && window.lbLucide.refresh) window.lbLucide.refresh();
		} else {
			lbAlert.toast({ color: 's', message: data.message || 'Senha alterada com sucesso.' });
		}

		if (typeof opts.onSuccess === 'function') opts.onSuccess(data.data || {});
		return true;
	};
})();
